import googleAnalytics from 'lib/GoogleAnalytics';
import eventLogger from 'lib/EventLogger';
import { EventType, SourcePages } from 'lib/EventSchema';
import { trackHomepageView as trackHomepageViewForCriteo } from 'lib/Criteo';
import { trackHomepageView as trackHomepageViewForAdwords } from 'lib/GoogleAdWords';

export function trackScreenView({ page }: { page: string }): void {
  googleAnalytics.event({
    category: 'screenview',
    action: page,
  });
}

// This event is used to prevent bounce rate increment if the user
// stays on the page for at least 15 secionds.
export function dontIncrementBounceRate(): void {
  googleAnalytics.event({
    category: '15 seconds',
    action: 'read',
    nonInteraction: false,
  });
}

export function trackScreenDimensions({ category }: { category: string }): void {
  googleAnalytics.event({
    category,
    action: 'viewed',
    label: `${window.innerWidth}x${window.innerHeight}`,
  });
}

export function trackHomepageView(): void {
  trackHomepageViewForCriteo();
  trackHomepageViewForAdwords();
}

// When the user is on the homepage and navigates to another page on Autolist this
// event should be fired.  Originally it was added so it could be used as an a/b test
// metric when we migrated the homepage.
export function trackHomePageNavigation(): void {
  googleAnalytics.event({
    category: 'home_page',
    action: 'navigation',
  });
}

export function trackHomepageAppDownloadClick(action: string, targetPage: string): void {
  googleAnalytics.event({
    category: 'home_page',
    action,
    nonInteraction: false,
  });

  eventLogger.log({
    event_type: EventType.homepageEngagement,
    engagement_type: 'download_app_click',
    source_page: SourcePages.Home,
    event_context: {
      target_page: targetPage,
    },
  });
}

// When the user is on the homepage and clicks on a "Trending Search", this event
// should be fired.
export function trackTrendingSearch(type: string): void {
  googleAnalytics.event({
    category: 'home_page',
    action: 'featured_trending_searches',
    label: type,
    nonInteraction: false,
  });
}

// When the user is on the homepage and clicks on a search in a section, this
// event should be fired.
export function trackHomePageFeaturedSearch(section: string, url: string): void {
  googleAnalytics.event({
    category: 'home_page',
    action: section,
    label: url,
    nonInteraction: false,
  });

  eventLogger.log({
    event_type: EventType.homepageEngagement,
    engagement_type: `${section}_click`,
    source_page: SourcePages.Home,
    event_context: {
      target_page: url,
    },
  });
}

// When the user is on the homepage and clicks Show More/Less in one of the
// featured searches sections, this event should be fired.
export function trackHomePageShowLinksToggle(section: string, label: string): void {
  googleAnalytics.event({
    category: 'home_page',
    action: section,
    label,
    nonInteraction: false,
  });

  eventLogger.log({
    event_type: EventType.homepageEngagement,
    source_page: SourcePages.Home,
    engagement_type: `${section}_${label}`,
  });
}

// When the user is on the homepage and performs a search via the search box in the header,
// then this event should be fired.
export function trackSearch({
  category,
  params = {},
}: {
  category: string;
  // eslint-disable-next-line camelcase
  params?: { make?: string; model?: string; body_type?: string };
}): void {
  let action;
  let label;

  if (params.make && params.model) {
    action = 'search_by_make_model';
    label = `${params.make} ${params.model}`;
  } else if (params.body_type) {
    action = 'search_by_body_type';
    label = params.body_type;
  } else {
    action = 'search_all';
  }

  googleAnalytics.event({
    category,
    action,
    label,
    nonInteraction: false,
  });
}

// When the user is on the homepage and clicks on a search action via the search box in the header,
// then this event should be fired.
export function trackSearchBoxClick(type: string): void {
  googleAnalytics.event({
    category: 'home_page',
    action: `search_box_by_${type}`,
    label: 'click',
    nonInteraction: false,
  });
}

export function trackGeolocationSuccess({
  category,
  location,
}: {
  category?: string;
  location: string;
}): void {
  googleAnalytics.event({
    category,
    action: 'location_success',
    label: location,
  });
}

export function trackGeolocationFailure({ category }: { category?: string }): void {
  googleAnalytics.event({
    category,
    action: 'location_fail',
  });
}

export function trackAutocompleteSuccess({
  category,
  location,
}: {
  category: string;
  location: string;
}): void {
  googleAnalytics.event({
    category,
    action: 'autocomplete_success',
    label: location,
  });
}

export function trackAppDownload({
  category,
  label,
  type,
}: {
  category: string;
  label: string;
  type: string;
}): void {
  if (!['ios', 'android'].includes(type)) {
    return;
  }

  googleAnalytics.event({
    category,
    action: `${type}_app_download`,
    label,
    nonInteraction: false,
  });
}

// This event should be triggered when the user is on the news & analysis page and clicks
// on the "News and Reviews" link in the header.
export function trackNewsClick(): void {
  googleAnalytics.event({
    category: 'newsandanalysis',
    action: 'news_click',
    nonInteraction: false,
  });
}

// This event should be triggered when the user is on the news & analysis page and clicks
// on the "Industry Studies" link in the header.
export function trackIndustryStudiesClick(): void {
  googleAnalytics.event({
    category: 'newsandanalysis',
    action: 'industry_studies_click',
    nonInteraction: false,
  });
}

// This event should be triggered when the user is on the news & analysis page and clicks
// on the "Affordability Rankings" link in the header.
export function trackAffordabilityRankingsClick(): void {
  googleAnalytics.event({
    category: 'newsandanalysis',
    action: 'affordability_rankings_click',
    nonInteraction: false,
  });
}

export function trackShare({
  category,
  type,
  platform,
  url,
}: {
  category: string;
  type: string;
  platform: string;
  url: string;
}): void {
  googleAnalytics.event({
    category,
    action: `${platform}_${type}_share`,
    nonInteraction: false,
  });

  eventLogger.log({
    event_type: EventType.socialShare,
    event_context: {
      page_type: type,
      share_platform: platform,
      url,
    },
  });
}

export function trackCtaClick(
  category: string,
  pageName: string,
  targetPage?: SourcePages | string
): void {
  googleAnalytics.event({
    category: 'content',
    action: 'cta_click',
  });

  eventLogger.log({
    event_type: EventType.contentEngagement,
    engagement_type: 'cta_click',
    source_page: category as SourcePages,
    event_context: {
      target_page: targetPage,
      page_name: pageName,
    },
  });
}

export function trackGuidesNavClick(engagementType: string): void {
  googleAnalytics.event({
    category: 'content',
    action: engagementType,
    label: 'guides',
  });

  eventLogger.log({
    event_type: EventType.contentEngagement,
    engagement_type: engagementType,
    source_page: SourcePages.GuidesLanding,
    event_context: {
      page_name: '/guides',
    },
  });
}

export function trackArticleClick(engagementType: string, targetPage: string): void {
  googleAnalytics.event({
    category: 'content',
    action: engagementType,
    label: 'guides',
  });

  eventLogger.log({
    event_type: EventType.contentEngagement,
    engagement_type: engagementType,
    source_page: SourcePages.GuidesLanding,
    event_context: {
      page_name: '/guides',
      target_page: targetPage,
    },
  });
}

export function trackRelatedClick(
  category: SourcePages,
  pageName: string,
  targetPage: string
): void {
  googleAnalytics.event({
    category: 'content',
    action: 'related_click',
  });

  eventLogger.log({
    event_type: EventType.contentEngagement,
    engagement_type: 'related_click',
    source_page: category,
    event_context: {
      page_name: pageName,
      target_page: targetPage,
    },
  });
}

export function trackArticleSummaryPagination({ page }: { page: string }): void {
  googleAnalytics.event({
    category: 'newsandanalysis',
    action: 'pagination',
    label: page,
    nonInteraction: false,
  });
}

export function trackArticleRssSubscription(): void {
  googleAnalytics.event({
    category: 'newsandanalysis',
    action: 'subscribe_rss',
  });
}

export function trackLeadFeedback({
  leadId,
  question,
  answer,
}: {
  leadId: number;
  question: string;
  answer: string;
}): void {
  eventLogger.log({
    event_type: EventType.leadFeedbackResponse,
    event_context: {
      question,
      answer,
      lead_id: leadId,
    },
  });
}

export function trackSlideshowNavigation(
  type: string,
  targetSlide: string,
  eventsData: {
    categoryName: SourcePages;
    pageName: string;
  }
): void {
  googleAnalytics.event({
    action: type,
    category: 'slideshow',
    label: targetSlide,
    nonInteraction: false,
  });

  eventLogger.log({
    event_type: EventType.engagement,
    engagement_type: type,
    source_page: eventsData.categoryName,
    event_context: {
      slide_number: targetSlide,
      page_name: eventsData.pageName,
    },
  });
}
